import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Mention, MentionsInput } from "react-mentions";
import Form from "react-bootstrap/Form";
import {
  addLeadNote,
  getListingLeadNotesData,
  getUsers,
} from "../../services/api";
import { pushToSelectedListingLeadNoteData } from "../../store/reducers/listingReducer";
import { formatDate } from "../../helpers/util";

interface IProps {
  currentListing: any;
}

const styles = {
  control: {
    backgroundColor: "#fff",
    fontSize: 14,
    fontWeight: "normal",
  },

  "&multiLine": {
    control: {
      minHeight: 63,
    },
    highlighter: {
      padding: 9,
      border: "1px solid transparent",
    },
    input: {
      padding: 9,
      border: "1px solid silver",
      lineHeight: "30px",
    },
  },

  "&singleLine": {
    display: "inline-block",
    width: 180,

    highlighter: {
      padding: 1,
      border: "2px inset transparent",
    },
    input: {
      padding: 1,
      border: "2px inset",
    },
  },

  suggestions: {
    list: {
      backgroundColor: "#792321",
      border: "1px solid rgba(0,0,0,0.15)",
      fontSize: 14,
      color: "#fffff",
    },
    item: {
      padding: "5px 15px",
      borderBottom: "1px solid rgba(0,0,0,0.15)",
      backgroundColor: "#e17471",
      color: "#ffffff",
      "&focused": {
        backgroundColor: "#792321",
      },
    },
  },
};

const mentionStyle = {
  backgroundColor: "#792321",
};

export const ListingLeadNotes = (props: IProps) => {
  const { currentListing } = props;
  const dispatch = useDispatch();

  const [note, setNote] = React.useState<string>("");
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const [selectedListingLeadNoteData, setSelectedListingLeadNoteData] =
    useState<any>(null);
  const logged = useSelector((state: any) => state.listingReducer.logged);

  React.useEffect(() => {
    (async () => {
      setErrorMessage("");
      setNote("");
      if (currentListing) {
        let listingLeadNotesResponse = await getListingLeadNotesData(
          currentListing.listing_unique_id
        );
        setSelectedListingLeadNoteData(listingLeadNotesResponse.data.data);
      }
    })();

    return () => {};
  }, [currentListing, logged]);

  async function fetchUsers(query: string, callback: any) {
    if (!query) return;
    let params: any = {};
    params["searchTerm"] = query;
    let usersResponse = await getUsers(params);
    if (usersResponse) {
      callback(usersResponse.data.data);
    } else {
      callback([]);
    }
  }

  function extractAllUserUUIDs(inputString: string) {
    const regex = /user:([\w-]+)/g;
    const matches = [];
    let match;

    while ((match = regex.exec(inputString)) !== null) {
      matches.push(match[1]);
    }
    return matches.join(", ");
  }

  function convertTextToLinks(inputText: string) {
    const regex = /\@\[([^\]]+)\]\(user:([^\)]+)\)/g;
    /*const replacedText = inputText.replace(
                regex,
                '<a href="user/$2">$1</a>'
            );
    */
    return inputText.replace(regex, '<a href="#">$1</a>');
  }

  async function addLeadNoteData() {
    setErrorMessage("");
    if (!note || note === "") {
      setErrorMessage("Note is required");
      return;
    }
    setErrorMessage("");
    let users = extractAllUserUUIDs(note);
    let params: any = {};
    params["note"] = convertTextToLinks(note);
    params["note_original"] = note;
    params["listing_unique_id"] = currentListing.listing_unique_id;
    params["users"] = users;
    let result = await addLeadNote(params);
    setNote("");
    if (!result?.data.success) {
      setErrorMessage("Save failed. Please try again.");
      return;
    }
    if (result) {
      setSelectedListingLeadNoteData((prev: any) => [
        ...prev,
        result?.data.data,
      ]);
    }
  }

  return (
    <div>
      <div className="grid grid-cols-1 gap-3">
        {selectedListingLeadNoteData?.map((note: any) => (
          <div key={note.public_id}>
            <div className="flex justify-between text-sm font-semibold">
              <div>{note.name}</div>
              <div>{formatDate(note.time_added)}</div>
            </div>
            <div
              className="mt-2 border p-2"
              dangerouslySetInnerHTML={{
                __html: note.note.replace(/\n\r?/g, "<br />"),
              }}
            ></div>
          </div>
        ))}
        {selectedListingLeadNoteData?.length < 1 && <div>No Notes</div>}
      </div>
      {logged && (
        <Form
          className="mt-6"
          onSubmit={(event) => {
            event.preventDefault();
            addLeadNoteData();
          }}
        >
          <Form.Group className="mb-3" controlId="leadNote.Note">
            <label className="text-sm font-medium text-gray-700">
              Add Note
            </label>
            {errorMessage !== "" && (
              <div className="text-red-500 text-xs p-3">{errorMessage}</div>
            )}
            <MentionsInput
              className="border px-4 py-2 outline-none w-full"
              style={styles}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Mention any user by typing `@` followed by at least one char"
              a11ySuggestionsListLabel={"Suggested users for mention"}
            >
              <Mention
                markup="@[__display__](user:__id__)"
                trigger="@"
                data={fetchUsers}
                renderSuggestion={(
                  suggestion,
                  search,
                  highlightedDisplay,
                  index,
                  focused
                ) => (
                  <div className={`user ${focused ? "focused" : ""}`}>
                    {highlightedDisplay}
                  </div>
                )}
                style={mentionStyle}
              />
            </MentionsInput>
          </Form.Group>
          <button
            type={"submit"}
            className={`py-2 px-3 text-white bg-primary`}
            onClick={(event) => {
              event.preventDefault();
              addLeadNoteData();
            }}
          >
            Save
          </button>
        </Form>
      )}
    </div>
  );
};
export default ListingLeadNotes;
